// Which statement periods a homeowner can ask for.
//
// **Monthly is an assumption, and a deliberate one.** Real billing
// cycles vary — an association may bill quarterly or annually — but no
// member endpoint exposes the assessment schedule's frequency, so
// there is nothing to read it from. Monthly is the safe guess: a month
// with nothing in it renders as a statement that says so, which is
// honest, whereas guessing quarterly on a monthly association would
// hide charges inside a period nobody asked for. Recorded as a future
// enhancement in `docs/LEDGER_FRONTEND_PLAN.md`.

export interface StatementPeriod {
  /** `"2026-08"` — the route param and the React key. */
  id: string;
  /** `"August 2026"`. */
  label: string;
  /** First day of the month, as a `NaiveDate` the API accepts. */
  from: string;
  /** Last day of the month. */
  to: string;
}

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** The period a `"2026-08"` id names, or null if it is not one. */
export const parsePeriod = (id?: string | null): StatementPeriod | null => {
  const match = /^(\d{4})-(\d{2})$/.exec((id ?? '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return periodFor(year, month);
};

const periodFor = (year: number, month: number): StatementPeriod => {
  // Day 0 of the next month is the last day of this one, and this is
  // local-time arithmetic on numbers rather than a parsed date string —
  // so there is no UTC midnight to shift the month boundary.
  const lastDay = new Date(year, month, 0).getDate();
  return {
    id: `${year}-${pad(month)}`,
    label: new Date(year, month - 1, 1).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    }),
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
};

/**
 * Every month from the account's first entry to now, newest first.
 *
 * `earliest` is the effective date of the oldest ledger entry — one
 * `GET /ledger?order=asc&per_page=1` gives it, which is why this needs
 * no new endpoint. An account with no entries yet gets the current
 * month alone rather than an empty list, so the screen has something to
 * show and the homeowner learns their account exists but is quiet.
 *
 * Capped at `maxMonths` so a decade-old account does not render 120
 * rows on first paint; the list offers to go further back.
 */
export const statementPeriods = (
  earliest?: string | null,
  today: Date = new Date(),
  maxMonths = 24
): StatementPeriod[] => {
  const nowYear = today.getFullYear();
  const nowMonth = today.getMonth() + 1;

  const match = /^(\d{4})-(\d{2})/.exec((earliest ?? '').trim());
  const startYear = match ? Number(match[1]) : nowYear;
  const startMonth = match ? Number(match[2]) : nowMonth;

  const span = (nowYear - startYear) * 12 + (nowMonth - startMonth) + 1;
  const count = Math.max(1, Math.min(span, maxMonths));

  const periods: StatementPeriod[] = [];
  for (let i = 0; i < count; i += 1) {
    // Walk back from this month. `new Date(y, m, 1)` normalises a
    // negative month into the previous year on its own.
    const d = new Date(nowYear, nowMonth - 1 - i, 1);
    periods.push(periodFor(d.getFullYear(), d.getMonth() + 1));
  }
  return periods;
};
